import { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';

export default function ButtholePredictorApp() {
  const [stage, setStage] = useState('upload');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [confidence, setConfidence] = useState(0);

  const loadingMessages = [
    'Analyzing facial features...',
    'Detecting moles...',
    'Reviewing mole patterns...',
    'Cross-referencing butthole database...',
    'Consulting proctologists...',
    'Finalizing prediction...'
  ];

  useEffect(() => {
    if (stage === 'loading') {
      let index = 0;
      setLoadingMessage(loadingMessages[0]);
      
      const interval = setInterval(() => {
        index++;
        if (index < loadingMessages.length) {
          setLoadingMessage(loadingMessages[index]);
        } else {
          clearInterval(interval);
          const randomConfidence = Math.floor(Math.random() * 15) + 85;
          setConfidence(randomConfidence);
          setTimeout(() => setStage('results'), 500);
        }
      }, 1200);

      return () => clearInterval(interval);
    }
  }, [stage]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleSubmit = () => {
    if (uploadedImage) {
      setStage('loading');
    }
  };

  const handleReset = () => {
    setStage('upload');
    setUploadedImage(null);
    setRevealed(false);
    setConfidence(0);
  };

  const generateButthole = () => {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #fce7f3 0%, #fef3c7 100%)',
        borderRadius: '8px'
      }}>
        <div style={{ position: 'relative', width: '128px', height: '128px' }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle, #f9a8d4 0%, #fb7185 50%, #d97706 100%)',
            borderRadius: '50%'
          }}></div>
          <div style={{
            position: 'absolute',
            inset: '16px',
            background: 'radial-gradient(circle, #fb7185 0%, #b45309 50%, #78350f 100%)',
            borderRadius: '50%'
          }}></div>
          <div style={{
            position: 'absolute',
            inset: '32px',
            background: 'radial-gradient(circle, #b45309 0%, #1c1917 100%)',
            borderRadius: '50%'
          }}></div>
          <div style={{
            position: 'absolute',
            inset: '48px',
            background: '#0c0a09',
            borderRadius: '50%'
          }}></div>
        </div>
      </div>
    );
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 50%, #3b82f6 100%)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },
    header: {
      padding: '24px',
      textAlign: 'center'
    },
    title: {
      fontSize: '3rem',
      fontWeight: 'bold',
      color: 'white',
      margin: 0,
      letterSpacing: '-0.02em'
    },
    main: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    },
    card: {
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      padding: '48px',
      maxWidth: '600px',
      width: '100%'
    },
    cardLarge: {
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      padding: '48px',
      maxWidth: '1000px',
      width: '100%'
    },
    subtitle: {
      fontSize: '1.875rem',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '24px',
      textAlign: 'center'
    },
    description: {
      color: '#6b7280',
      marginBottom: '32px',
      textAlign: 'center',
      fontSize: '1rem'
    },
    uploadArea: {
      border: '4px dashed',
      borderColor: dragActive ? '#a855f7' : uploadedImage ? '#10b981' : '#d1d5db',
      background: dragActive ? '#faf5ff' : uploadedImage ? '#f0fdf4' : '#f9fafb',
      borderRadius: '12px',
      padding: '48px',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    uploadedImg: {
      maxHeight: '256px',
      margin: '0 auto',
      borderRadius: '8px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    },
    successText: {
      color: '#10b981',
      fontWeight: '600',
      marginTop: '16px'
    },
    uploadIcon: {
      margin: '0 auto 16px',
      color: '#9ca3af'
    },
    fileButton: {
      display: 'inline-block',
      padding: '12px 24px',
      background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
      color: 'white',
      fontWeight: '600',
      borderRadius: '8px',
      cursor: 'pointer',
      border: 'none',
      fontSize: '1rem',
      transition: 'transform 0.2s ease'
    },
    submitButton: {
      width: '100%',
      marginTop: '32px',
      padding: '16px 32px',
      background: 'linear-gradient(135deg, #9333ea 0%, #2563eb 100%)',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '1.125rem',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      transition: 'transform 0.2s ease'
    },
    spinner: {
      width: '80px',
      height: '80px',
      margin: '0 auto 24px',
      border: '4px solid #e5e7eb',
      borderTop: '4px solid #a855f7',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    },
    loadingText: {
      fontSize: '1.5rem',
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '8px'
    },
    resultsGrid: {
      display: 'grid',
      gridTemplateColumns: window.innerWidth >= 768 ? '1fr 1fr' : '1fr',
      gap: '24px',
      marginBottom: '32px'
    },
    resultCard: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    },
    resultLabel: {
      fontSize: '0.875rem',
      fontWeight: '600',
      color: '#6b7280',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      textAlign: 'center'
    },
    imageContainer: {
      aspectRatio: '1',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer'
    },
    revealBox: {
      width: '100%',
      height: '100%',
      background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: '16px',
      marginBottom: '32px'
    },
    statBox: {
      borderRadius: '8px',
      padding: '16px',
      textAlign: 'center'
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @media (max-width: 768px) {
          .title { font-size: 2rem !important; }
        }
      `}</style>

      <header style={styles.header}>
        <h1 style={styles.title} className="title">WHATSMYBUTTHOLE.COM</h1>
      </header>

      <main style={styles.main}>
        {stage === 'upload' && (
          <div style={styles.card}>
            <h2 style={styles.subtitle}>Discover Your Butthole</h2>
            <p style={styles.description}>
              Upload a photo of your face and our advanced AI will predict what your butthole looks like
            </p>

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              style={styles.uploadArea}
            >
              {uploadedImage ? (
                <div>
                  <img
                    src={uploadedImage}
                    alt="Uploaded"
                    style={styles.uploadedImg}
                  />
                  <p style={styles.successText}>Image uploaded successfully!</p>
                </div>
              ) : (
                <>
                  <Upload style={styles.uploadIcon} size={64} />
                  <p style={{ color: '#6b7280', marginBottom: '16px', fontWeight: '500' }}>
                    Drag and drop your photo here
                  </p>
                  <p style={{ color: '#9ca3af', marginBottom: '16px' }}>or</p>
                  <label style={styles.fileButton}>
                    Choose File
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileInput}
                      style={{ display: 'none' }}
                    />
                  </label>
                </>
              )}
            </div>

            {uploadedImage && (
              <button
                onClick={handleSubmit}
                style={styles.submitButton}
                onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
                onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
              >
                Predict My Butthole
              </button>
            )}
          </div>
        )}

        {stage === 'loading' && (
          <div style={styles.card}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>{loadingMessage}</p>
          </div>
        )}

        {stage === 'results' && (
          <div style={styles.cardLarge}>
            <h2 style={styles.subtitle}>Prediction Complete</h2>
            <p style={{ color: '#9333ea', fontWeight: '600', fontSize: '1.125rem', marginBottom: '32px', textAlign: 'center' }}>
              {confidence}% Confidence
            </p>

            <div style={styles.resultsGrid}>
              <div style={styles.resultCard}>
                <h3 style={styles.resultLabel}>Your Face</h3>
                <div style={styles.imageContainer}>
                  <img
                    src={uploadedImage}
                    alt="Your face"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>

              <div style={styles.resultCard}>
                <h3 style={styles.resultLabel}>Predicted Butthole</h3>
                <div
                  style={styles.imageContainer}
                  onClick={() => setRevealed(!revealed)}
                >
                  {!revealed ? (
                    <div style={styles.revealBox}>
                      <div>
                        <p style={{ color: 'white', fontWeight: 'bold', fontSize: '1.25rem', marginBottom: '8px' }}>
                          Click to Reveal
                        </p>
                        <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>or swipe on mobile</p>
                      </div>
                    </div>
                  ) : (
                    generateButthole()
                  )}
                </div>
              </div>
            </div>

            <div style={styles.statsGrid}>
              <div style={{ ...styles.statBox, background: '#faf5ff' }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#9333ea' }}>
                  {Math.floor(Math.random() * 3) + 2}"
                </p>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Estimated Diameter</p>
              </div>
              <div style={{ ...styles.statBox, background: '#fdf2f8' }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ec4899' }}>
                  {Math.floor(Math.random() * 50) + 20}
                </p>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Wrinkle Count</p>
              </div>
              <div style={{ ...styles.statBox, background: '#eff6ff' }}>
                <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#3b82f6' }}>
                  {Math.floor(Math.random() * 40) + 30}°
                </p>
                <p style={{ fontSize: '0.75rem', color: '#6b7280' }}>Pucker Angle</p>
              </div>
            </div>

            <button
              onClick={handleReset}
              style={styles.submitButton}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              Try Another Face
            </button>
          </div>
        )}
      </main>
    </div>
  );
}